import { supabase } from '../config/supabase';

export const clientService = {
  // Get all clients for current user
  async getClients(activeOnly = false) {
    let query = supabase
      .from('clients')
      .select('*')
      .order('name', { ascending: true });

    if (activeOnly) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  },

  // Get single client with project count
  async getClient(id) {
    const { data: client, error } = await supabase
      .from('clients')
      .select(`
        *,
        projects(id, name, status)
      `)
      .eq('id', id)
      .single();

    if (error) throw error;
    return client;
  },

  // Create new client
  async createClient(clientData) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('clients')
      .insert([{
        name: clientData.name,
        company_name: clientData.companyName,
        email: clientData.email,
        phone: clientData.phone,
        address: clientData.address,
        city: clientData.city,
        postal_code: clientData.postalCode,
        country: clientData.country || 'Estonia',
        contact_person: clientData.contactPerson,
        notes: clientData.notes,
        is_active: clientData.isActive !== undefined ? clientData.isActive : true,
        created_by: user.id
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Update client
  async updateClient(id, updates) {
    const { data, error } = await supabase
      .from('clients')
      .update({
        name: updates.name,
        company_name: updates.companyName,
        email: updates.email,
        phone: updates.phone,
        address: updates.address,
        city: updates.city,
        postal_code: updates.postalCode,
        country: updates.country,
        contact_person: updates.contactPerson,
        notes: updates.notes,
        is_active: updates.isActive
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Delete client
  async deleteClient(id) {
    // First check if client has projects
    const { data: projects, error: projectError } = await supabase
      .from('projects')
      .select('id')
      .eq('client_id', id);

    if (projectError) throw projectError;

    if (projects && projects.length > 0) {
      throw new Error(`Cannot delete client. There are ${projects.length} projects associated with this client.`);
    }

    const { error } = await supabase
      .from('clients')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return true;
  },

  // Toggle client active status
  async toggleClientStatus(id, isActive) {
    const { data, error } = await supabase
      .from('clients')
      .update({ is_active: isActive })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Search clients
  async searchClients(searchTerm) {
    const { data, error } = await supabase
      .from('clients')
      .select('*')
      .or(`name.ilike.%${searchTerm}%,company_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`)
      .order('name', { ascending: true });

    if (error) throw error;
    return data;
  },

  // Get client statistics
  async getClientStatistics() {
    const { data: clients, error } = await supabase
      .from('clients')
      .select(`
        id,
        name,
        is_active,
        projects(id, status)
      `);

    if (error) throw error;

    const totalClients = clients.length;
    const activeClients = clients.filter(c => c.is_active).length;
    const inactiveClients = totalClients - activeClients;

    // Calculate project counts per client
    const clientsWithProjectCounts = clients.map(client => ({
      ...client,
      projectCount: client.projects?.length || 0,
      activeProjects: client.projects?.filter(p => 
        ['Draft', 'Released for Production'].includes(p.status)
      ).length || 0
    }));

    // Top clients by project count
    const topClients = clientsWithProjectCounts
      .sort((a, b) => b.projectCount - a.projectCount)
      .slice(0, 5);

    return {
      overview: {
        totalClients,
        activeClients,
        inactiveClients
      },
      topClients,
      clientsWithProjectCounts
    };
  }
};

export default clientService;
